/* global CVP */

// ## MVPDConfig
//
// For processing Adobe's MVPD list and the property's MVPDConfig.
//
// Adobe's setConfig callback gets [ { id, logoUrl }, ... ]
// Adobe's displayProviderDialog gets [ { ID, logoURL }, ... ]
// mvpdconfig has [ { id, logoURL }, ... ]

'use strict';

var App = require('./app');
var Promise = require('./vendor/machina.promise');
var _ = require('./vendor/underscore');
var _logger = require('./logger');
var Util = require('./util');
var SimpleEmitter = require('./emitter');


var MVPDConfig = new SimpleEmitter();
var KNOWN_PLATFORMS = [ 'flash', 'Android', 'iOS', 'Win8', 'Xbox', 'appletv', 'roku', 'firetv' ];
var platform = '';
var mvpdConfigUrl = '';
var requestorId = '';
var propertyIds = [];
var propertyMVPDs = [];
var sanitizedMVPDs = [];
var darkMvpdIds = [];
var hiddenMvpdIds = [];
var cacheBustValue = (Math.round(_.now() / 300000)).toString(36);

var waitingForWhitelistMerge = new Promise();
var waitingForPropertyMvpds = new Promise();
var waitingForVendorMvpds = new Promise();

var geoServiceUrl;
var darkPhaseUrl;
var darkPhaseCallbackName = 'cvp_onDarkPhaseReceived';
var waitingForDarkPhaseConfig = new Promise();
var darkPhasePromise = new Promise();
var mvpdJsonpCallbackName = 'cvp_onMVPDConfigReceived';

function unwrapJsonp(callbackName, success, failure) {
	return function handleJsonp(jsonpString) {
		if (!jsonpString) {
			return failure('Unsuccessful attempt to obtain JSONP');
		}

		if (jsonpString.indexOf(callbackName) === -1) {
			return failure('JSONP callback name not found in response');
		}

		try {
			var jsonpFn = new Function(callbackName, jsonpString);
			// function jsonpFn(darkPhaseCallbackName) {
			// 	darkPhaseCallbackName && darkPhaseCallbackName(json);
			// }

			try {
				jsonpFn(function (json) {
					_logger.log('JSONP response for "' + callbackName + '":', json);
					success(json);
				});
			}
			catch (callbackException) {
				failure(callbackException);
			}
		}
		catch (fnCreationException) {
			failure(fnCreationException);
		}
	};
}


function determineMvpdConfigUrl(site, env) {
	var url = App.CVP_CONFIG_ROOT + '/' + site + '/tve' + (env === App.CONFIG_ENV_PREPROD ? '/preprod' : '') + '/mvpdconfig.js';
	url = Util.augmentQueryString(url, {
		_ : cacheBustValue
	});
	return url;
}

function getMvpdConfigUrl() {
	return mvpdConfigUrl;
}

function getGeoServiceUrl() {
	return geoServiceUrl;
}

function getDarkPhaseUrl() {
	return darkPhaseUrl;
}

function requestDarkPhaseData() {
	return darkPhasePromise;
}

// `normalizeMVPD` is an internal method, used to append additional
// properties to an existing MVPD object/entry, if necessary.
function normalizeMVPD(mvpd) {

	// update `iFrameRequired`
	if (_.isString(mvpd.iFrameRequired)) {
		mvpd.iFrameRequired = mvpd.iFrameRequired === 'true';
	}

	// cast `iFrameWidth` to num
	if (_.isString(mvpd.iFrameWidth)) {
		mvpd.iFrameWidth = Util.toInt(mvpd.iFrameWidth);
	}

	// cast `iFrameHeight` to num
	if (_.isString(mvpd.iFrameHeight)) {
		mvpd.iFrameHeight = Util.toInt(mvpd.iFrameHeight);
	}

	// update `logoURL`
	if (!('logoURL' in mvpd) && 'logoUrl' in mvpd) {
		mvpd.logoURL = mvpd.logoUrl;
	}

	// update `ID`
	if (!(App.MVPD_ID_KEY in mvpd) && 'id' in mvpd) {
		mvpd[App.MVPD_ID_KEY] = mvpd.id;
	}

	return mvpd;
}

function interpretMvpdConfigEntry(config) {
	var newConfig = {};

	function getDefaultProperties() {
		return _.omit(config, ['country', 'format'].concat(_.without(KNOWN_PLATFORMS, platform)));
	}

	function toUsefulData(memo, value, key, list) {
		if (!_.isEmpty(value) || _.isString(value)) {
			memo[key] = value;
		}

		return memo;
	}

	function getFormatProperties() {
		// NOTE: 'flash' (web) picker/cobrand images can be used for second-screen
		var flashFormatObj = _.reduce(_.findWhere(config.format, { os : 'flash' }), toUsefulData, {});
		// ... but there may be platform-specified Picker images too!
		var platformFormatObj = _.reduce(_.findWhere(config.format, { os : platform }), toUsefulData, {});
		// ... merge, with a priority on platform-specific
		return _.extend({}, flashFormatObj, platformFormatObj);
	}

	function getCountries() {
		var countries = [ App.DEFAULT_COUNTRY ];
		if (_.isString(config.country)) {
			// Translate config's 'country' string into an array.
			countries = _.compact(
				_.uniq(
					_.map(config.country.split(App.CONFIG_MULTIVALUE_DELIM), _.compose(Util.toUpper, Util.trim))
				)
			);
		}
		return countries;
	}

	newConfig = normalizeMVPD(_.extend(getDefaultProperties(), getFormatProperties()));

	newConfig.countries = getCountries();

	return newConfig;
}

function updateClientConfig(mvpds) {
	if (_.isEmpty(mvpds) || !_.isArray(mvpds)) {
		_logger.error('MVPDConfig:updateClientConfig failed to receive list of MVPDs!');
		waitingForPropertyMvpds.reject({
			errorCode : 'CFG012',
			errorId : 'CFG012',
			errorString : 'invalid client mvpd list',
			errorCategory : App.ErrorCategory.BLOCKED,
			resolution : App.Resolution.TRY_AGAIN_LATER
		});
		return;
	}
	propertyMVPDs = _.map(mvpds, interpretMvpdConfigEntry);
	propertyIds = _.pluck(propertyMVPDs, App.MVPD_ID_KEY);
	_logger.log('MVPDConfig:updateClientConfig', propertyIds.length + ' MVPDs listed in mvpdconfig: ' + propertyIds.join(', '));
	waitingForPropertyMvpds.fulfill(propertyMVPDs);
}

function updateVendorConfig(mvpds) {
	if (_.isEmpty(mvpds) || !_.isArray(mvpds)) {
		_logger.error('MVPDConfig:updateVendorConfig failed to receive list of MVPDs!');
		waitingForVendorMvpds.reject({
			errorCode : 'CFG002',
			errorId : 'CFG002',
			errorString : 'invalid vendor mvpd list',
			errorCategory : App.ErrorCategory.BLOCKED,
			resolution : App.Resolution.TRY_AGAIN_LATER
		});
		return;
	}
	mvpds = _.map(mvpds, normalizeMVPD);
	var mvpdIds = _.pluck(mvpds, App.MVPD_ID_KEY);
	_logger.log('MVPDConfig:updateVendorConfig', mvpdIds.length + ' MVPDs provided from vendor: ' + mvpdIds.join(', '));
	waitingForVendorMvpds.fulfill(mvpds);
}

function fulfillWaitingForDarkPhase(value) {
	waitingForDarkPhaseConfig.fulfill(value);
}

function darkPhaseLoadFailure() {
	_logger.warn('Failed to obtain dark phase data');

	fulfillWaitingForDarkPhase(true);
	darkPhasePromise.reject();
}

function darkPhaseLoadSuccess(data) {
	_logger.log('Successfully obtained dark phase data');

	if (_.isObject(data) && !_.isEmpty(data)) {
		darkMvpdIds = _.keys(data);
		_logger.debug('MVPDConfig found dark phase providers', darkMvpdIds);

		MVPDConfig.emit('darkPhaseLoadSuccess');

		fulfillWaitingForDarkPhase(true);
		darkPhasePromise.fulfill(data);
	}
	else {
		_logger.debug('MVPDConfig found no dark phase providers');

		MVPDConfig.emit('darkPhaseLoadFailure');

		darkPhaseLoadFailure();
	}
}

function mvpdConfigLoadFailure() {
	_logger.error('Failed to obtain MVPDConfig');

	MVPDConfig.emit('mvpdConfigLoadFailure');

	waitingForPropertyMvpds.reject({
		errorCode : 'CFG010',
		errorId : 'CFG010',
		errorString : 'mvpdconfig load failure',
		errorCategory : App.ErrorCategory.BLOCKED,
		resolution : App.Resolution.TRY_AGAIN_LATER
	});
}

function mvpdConfigLoadSuccess(data) {
	_logger.log('Successfully obtained MVPDConfig');

	if (_.isUndefined(data) || !_.isString(data.brand)) {
		mvpdConfigLoadFailure();
		throw new Error(mvpdJsonpCallbackName + ' did not receive valid data or brand');
	}

	if (data.brand !== requestorId) {
		mvpdConfigLoadFailure();
		throw new Error(mvpdJsonpCallbackName + ' received a "brand" (' + data.brand + ') that did not match "requestorId" (' + requestorId + ')');
	}

	if (!_.isArray(data.mvpd)) {
		mvpdConfigLoadFailure();
		throw new Error(mvpdJsonpCallbackName + ' received invalid "mvpd" data');
	}

	// Update geolocation service URL.
	if (_.isObject(data.geo) && _.isString(data.geo.url)) {
		geoServiceUrl = data.geo.url;
		_logger.debug('MVPDConfig geo service URL obtained from MVPDConfig: ', geoServiceUrl);
	}
	else {
		_logger.debug('MVPDConfig geo service URL was not obtained from MVPDConfig.');
	}

	// Update darkPhase URL.
	if (_.isObject(data.mvpdHelper) && _.isString(data.mvpdHelper.url)) {
		darkPhaseUrl = Util.augmentQueryString(data.mvpdHelper.url, {
			_ : cacheBustValue
		});

		_logger.debug('MVPDConfig darkPhase URL obtained from MVPDConfig: ', darkPhaseUrl);

		MVPDConfig.emit('darkPhaseLoadAttempt', {
			darkPhaseUrl : darkPhaseUrl
		});

		// CVP.Ajax.getJSONP({
		// 	url: darkPhaseUrl,
		// 	jsonpCallback: darkPhaseCallbackName,
		// 	timeout: DARK_PHASE_TIMEOUT,
		// 	success: darkPhaseLoadSuccess,
		// 	error: darkPhaseLoadFailure
		// });

		CVP.Request.get({
			url : darkPhaseUrl
		}).then(unwrapJsonp(darkPhaseCallbackName, darkPhaseLoadSuccess, darkPhaseLoadFailure), darkPhaseLoadFailure);

	}
	else {
		_logger.debug('MVPDConfig darkPhase URL was not obtained from MVPDConfig.');
		fulfillWaitingForDarkPhase(false);
	}

	MVPDConfig.emit('mvpdConfigLoadSuccess');

	updateClientConfig(data.mvpd);
}


function load(options) {

	// `requestorId` is required.
	requestorId = options.requestorId;

	// `platform` is required for Clientless; inferred from SWF/JS AccessEnabler.
	platform = options.platform;

	if (!platform) {
		throw new Error('The "platform" was undetermined.  Cannot proceed.');
	}

	// Configure hidden MVPDs, like TempPass, from init.
	hiddenMvpdIds = options.hiddenMvpdIds || [];

	// Determine `mvpdConfigUrl` from provided options.
	mvpdConfigUrl = options.mvpdConfigUrl;

	var site = options.site;
	var configEnv = options.configEnv;

	if (site && !_.isString(mvpdConfigUrl)) {
		mvpdConfigUrl = determineMvpdConfigUrl(site, configEnv);
	}

	if (!mvpdConfigUrl) {
		_logger.error('MVPDConfig:load', 'Could not determine `mvpdConfigUrl` for site "' + site + '"');
		mvpdConfigLoadFailure();
		return;
	}

	MVPDConfig.emit('mvpdConfigLoadAttempt', {
		requestorId : requestorId,
		mvpdConfigUrl : mvpdConfigUrl
	});

	_logger.log('MVPDConfig:load', 'Requesting mvpdconfig from ' + mvpdConfigUrl);

	// Request MVPD Config.
	// CVP.Ajax.getJSONP({
	// 	url: mvpdConfigUrl,
	// 	jsonpCallback: mvpdJsonpCallbackName,
	// 	timeout: MVPD_CONFIG_TIMEOUT,
	// 	success: mvpdConfigLoadSuccess,
	// 	error: mvpdConfigLoadFailure
	// });

	CVP.Request.get({
		url : mvpdConfigUrl
	}).then(unwrapJsonp(mvpdJsonpCallbackName, mvpdConfigLoadSuccess, mvpdConfigLoadFailure), mvpdConfigLoadFailure);

}

function fail(err) {
	_logger.error('MVPDConfig failure', err);
	waitingForWhitelistMerge.reject(err);
}

/**
 * @param {Array} res = [ vendorMVPDs, clientMVPDs ]
 */
function mergeLists(res) {
	var vendorMVPDs = res[0];
	var clientMVPDs = res[1];

	var diffMvpds = _.difference(_.pluck(vendorMVPDs, App.MVPD_ID_KEY), propertyIds);
	_logger.log('MVPDConfig:mergeLists', diffMvpds.length + ' vendor-provided MVPDs did not appear in client mvpdconfig: ' + diffMvpds.join(', '));

	// For each vendor mvpd that exists in list of property mvpds (whitelist),
	// copy vendor props, override with property props, and add back to list.
	_.each(vendorMVPDs, function (vendorMVPD) {
		var propertyIdsIndex = _.indexOf(propertyIds, vendorMVPD[App.MVPD_ID_KEY]);
		if (propertyIdsIndex !== -1) {
			var propertyMVPD = clientMVPDs[propertyIdsIndex];
			var mvpd = _.extend({}, vendorMVPD, propertyMVPD);
			sanitizedMVPDs.push(mvpd);
		}
	});

	_logger.log('MVPDConfig:mergeLists', 'configured MVPDs (' + sanitizedMVPDs.length + '): ' + _.pluck(sanitizedMVPDs, App.MVPD_ID_KEY).join(', '));

	waitingForWhitelistMerge.fulfill();
}

function isDarkMvpd(mvpd) {
	return _.contains(darkMvpdIds, mvpd[App.MVPD_ID_KEY]);
}

function isHiddenMvpd(mvpd) {
	return _.contains(hiddenMvpdIds, mvpd[App.MVPD_ID_KEY]);
}

function getWhitelistedMVPDs(filter) {
	var mvpds = darkMvpdIds.length ? _.reject(sanitizedMVPDs, isDarkMvpd) : sanitizedMVPDs.slice();

	return filter ? _.filter(mvpds, filter) : mvpds;
}

function getVisibleMVPDs(filter) {
	filter = filter || _.identity;

	var combofilter = function (mvpd) {
		return filter(mvpd) || isDarkMvpd(mvpd);
	};

	var mvpds = hiddenMvpdIds.length ? _.reject(sanitizedMVPDs, isHiddenMvpd) : sanitizedMVPDs.slice();

	return _.filter(mvpds, combofilter);
}

// `getMvpdConfigEntry` simply returns the property's mvpdconfig entry for the MVPD ID provided.
// returning a copy so the original cannot be modified
function getMvpdConfigEntry(id) {

	if (_.isNull(id)) {
		// short-circuit if invalid MVPD ID
		return undefined;
	}

	if (_.isUndefined(id)) {
		// return all entries
		return propertyMVPDs.slice();
	}

	var index = _.indexOf(propertyIds, id);

	if (index === -1) {
		// return nothing if ID not found in mvpdconfig
		return undefined;
	}

	return _.extend({}, propertyMVPDs[index]);
}

var mergingAfterReceivingBothMvpdLists = Promise.all(waitingForVendorMvpds, waitingForPropertyMvpds).then(mergeLists, fail);
var mvpdConfigReady = Promise.all(waitingForWhitelistMerge, waitingForDarkPhaseConfig);

_.extend(MVPDConfig, {
	ready : mvpdConfigReady,
	load : load,
	getGeoServiceUrl : getGeoServiceUrl,
	getDarkPhaseUrl : getDarkPhaseUrl,
	requestDarkPhaseData : requestDarkPhaseData,
	updateClientConfig : updateClientConfig,
	updateVendorConfig : updateVendorConfig,
	getWhitelistedMVPDs : getWhitelistedMVPDs,
	getVisibleMVPDs : getVisibleMVPDs,
	getMvpdConfigUrl : getMvpdConfigUrl,
	getMvpdConfigEntry : getMvpdConfigEntry
});


module.exports = MVPDConfig;
